import { AppCheckbox, AppDialog, AppInput, AppSelect } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Label } from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { toRecruitmentTypeFormValue } from '@inquiries/-lib/recruitment-type-options'
import { Save, Search } from 'lucide-react'
import {
  INQUIRY_NO_DEAL_LABEL,
  INQUIRY_RECRUITMENT_TYPE_LABEL_MAP,
  INQUIRY_START_ASAP_LABELS,
  RECRUITMENT_TYPE_VALUES,
} from 'shared'
import { AppWorkLocationCheckboxGroup } from '@/components/AppWorkLocationCheckboxGroup'
import { useFormattedNumberInput } from '@/hooks/useFormattedNumberInput'
import { useInquirySearchDialog } from './hooks/useInquirySearchDialog'

/** 募集形態の検索条件。「指定なし」は AppSelect が空文字を値にできないため 'all' で表す（主管部門の「すべて」と同じ） */
const recruitmentTypeSearchOptions = [
  { value: 'all', label: '指定なし' },
  ...RECRUITMENT_TYPE_VALUES.map((value) => ({ value, label: INQUIRY_RECRUITMENT_TYPE_LABEL_MAP[value] })),
]

interface InquirySearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSearch: (searchText: string) => void
  currentSearchText?: string
}

export function InquirySearchDialog({ open, onOpenChange, onSearch, currentSearchText }: InquirySearchDialogProps) {
  const {
    step,
    queryName,
    formState,
    isSaveEnabled,
    primaryDivisionOptions,
    inquiryStatusNames,
    setStep,
    setQueryName,
    setFormState,
    handleOpenChange,
    handleSearch,
    handleSaveQuery,
    handleStatusToggle,
  } = useInquirySearchDialog({
    open,
    onOpenChange,
    onSearch,
    currentSearchText,
  })

  const amountMinInputProps = useFormattedNumberInput(formState.estimatedAmountMin, (raw) =>
    setFormState((prev) => ({ ...prev, estimatedAmountMin: raw })),
  )
  const amountMaxInputProps = useFormattedNumberInput(formState.estimatedAmountMax, (raw) =>
    setFormState((prev) => ({ ...prev, estimatedAmountMax: raw })),
  )

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={step === 1 ? '詳細検索' : '検索条件を保存'}
      className='sm:max-w-5xl max-h-[90vh] overflow-y-auto'
      footerOptions={
        step === 1
          ? [
              {
                label: '名前を付けて保存',
                onClick: () => setStep(2),
                variant: 'save' as const,
                disabled: !isSaveEnabled,
                icon: <Save />,
              },
              { label: 'キャンセル', onClick: () => onOpenChange(false), variant: 'outline' },
              {
                label: '検索',
                onClick: handleSearch,
                icon: <Search />,
              },
            ]
          : [
              { label: 'キャンセル', onClick: () => setStep(1), variant: 'outline' },
              {
                label: '保存して検索',
                onClick: handleSaveQuery,
                variant: 'save' as const,
                disabled: !queryName,
                icon: <Save />,
              },
            ]
      }
    >
      {step === 1 ? (
        <div className='space-y-6'>
          <div className='space-y-4'>
            <AppInput
              id='query'
              label='含む（複数指定時は半角スペース区切り）'
              placeholder='例: プロジェクト名 キーワード'
              value={formState.query}
              onChange={(e) => setFormState((prev) => ({ ...prev, query: e.target.value }))}
            />

            <AppInput
              id='exclude'
              label='含まない（複数指定時は半角スペース区切り）'
              placeholder='例: テスト サンプル'
              value={formState.exclude}
              onChange={(e) => setFormState((prev) => ({ ...prev, exclude: e.target.value }))}
            />

            <div className='space-y-2'>
              <AppSelect
                id='primaryDivision'
                label='主管部門'
                value={formState.primaryDivisionId?.toString() || 'all'}
                onValueChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    primaryDivisionId: value === 'all' ? null : Number(value),
                  }))
                }
                options={[{ value: 'all', label: 'すべて' }, ...primaryDivisionOptions]}
              />
              <AppCheckbox
                label='自分の部署'
                checked={formState.isMyDivision}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, isMyDivision: checked as boolean }))}
                labelClassName='font-normal'
              />
            </div>

            <div className='space-y-2'>
              <div className='flex gap-4'>
                <AppInput
                  id='serviceManagerName'
                  label='SMG担当'
                  placeholder='例: 鈴木 花子'
                  value={formState.serviceManagerName}
                  onChange={(e) => setFormState((prev) => ({ ...prev, serviceManagerName: e.target.value }))}
                />
                <AppInput
                  id='salesMemberName'
                  label='営業担当'
                  placeholder='例: 山田 太郎'
                  value={formState.salesMemberName}
                  onChange={(e) => setFormState((prev) => ({ ...prev, salesMemberName: e.target.value }))}
                />
              </div>
              <AppCheckbox
                label='自身の担当'
                checked={formState.isMyAssignment}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, isMyAssignment: checked as boolean }))}
                labelClassName='font-normal'
              />
              <AppCheckbox
                label={INQUIRY_NO_DEAL_LABEL}
                checked={formState.isNoDeal}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, isNoDeal: checked as boolean }))}
                labelClassName='font-normal'
              />
            </div>

            <div className='space-y-2'>
              <Label className='text-xs text-muted-foreground'>ステータス</Label>
              <div className='flex flex-wrap gap-4'>
                {inquiryStatusNames.map((statusName) => (
                  <AppCheckbox
                    key={statusName}
                    label={statusName}
                    checked={formState.inquiryStatusNames.includes(statusName)}
                    onCheckedChange={(checked) => handleStatusToggle(statusName, checked as boolean)}
                    labelClassName='font-normal'
                  />
                ))}
              </div>
            </div>

            <AppWorkLocationCheckboxGroup
              label='作業場所'
              value={formState.workLocation}
              onChange={(workLocation) => setFormState((prev) => ({ ...prev, workLocation }))}
              labelClassName='font-normal'
            />

            <div className='w-48'>
              <AppSelect
                id='recruitmentType'
                label='募集形態'
                value={formState.recruitmentType || 'all'}
                onValueChange={(value) =>
                  setFormState((prev) => ({ ...prev, recruitmentType: toRecruitmentTypeFormValue(value) }))
                }
                options={recruitmentTypeSearchOptions}
              />
            </div>

            <div className='flex items-end gap-4'>
              <div className='w-48'>
                <AppInput
                  id='startMonth'
                  label='開始月'
                  placeholder='YYYY/MM'
                  value={formState.startMonth}
                  onChange={(e) => setFormState((prev) => ({ ...prev, startMonth: e.target.value }))}
                />
              </div>
              <AppCheckbox
                label={INQUIRY_START_ASAP_LABELS.LABEL}
                checked={formState.isStartAsap}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, isStartAsap: checked === true }))}
                labelClassName='font-normal'
                containerClassName='pb-2'
              />
            </div>
            <div className='space-y-2'>
              <div className='flex items-end gap-2'>
                <div className='w-40'>
                  <AppInput id='estimatedAmountMin' label='想定金額' placeholder='下限' {...amountMinInputProps} />
                </div>
                <span className='text-sm text-muted-foreground shrink-0 pb-2'>～</span>
                <div className='w-40'>
                  <AppInput id='estimatedAmountMax' label='&#8203;' placeholder='上限' {...amountMaxInputProps} />
                </div>
                <span className='text-sm text-muted-foreground shrink-0 pb-2'>円</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='space-y-4'>
          <AppInput
            id='queryName'
            label='検索条件名'
            placeholder='指定した検索条件にわかりやすい名前をつけてください'
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
          />
        </div>
      )}
    </AppDialog>
  )
}
